import { useCallback, useContext, useEffect, useState } from "react";
import '../TimeTracker.css';
import SessionTable from "./SessionTable";
import SessionEditModal from "./SessionEditModal";
import {request, SESSION_TYPE_AKQUISE, SESSION_TYPE_BUCHHALTUNG} from "../timeTracking";
import authStorage from "../auth_storage";
import { useNavigate } from "react-router-dom";
import Time from "../Time";
import InfoPanel from "../InfoPanel";
import Popup from "../Popup";
import { useInterval } from "../hooks";
import {
    getData,
    startSession,
    stopSession,
    SESSION_TYPE_PROJECT,
    SESSION_TYPE_CLEANING,
    SESSION_TYPE_CALLING,
    SESSION_TYPE_TECHNIK,
    SESSION_TYPE_MARKETING,
    SESSION_TYPE_NEW_LEAD,
    SESSION_TYPE_FREI_TEXT
} from "../timeTracking";
import { ReducerContext, SET_STATUS } from "../reducer";
import { requestNotificationPermission, updateTimeNotification, closeTimeNotification } from "../notifications";

function Sessions() {
    const { state, dispatch } = useContext(ReducerContext);
    const [sessions, setSessions] = useState([]);
    const [skip, setSkip] = useState([]);
    const [loading, setLoading] = useState(true);
    const [btnDisabled, setBtnDisabled] = useState(true);
    const [time, setTime] = useState(0);
    const [editingSession, setEditingSession] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchSessions();
        loadStatus();
        requestNotificationPermission();
    }, []);

    useEffect(() => {
        if (state.status?.session) {
            let name = "";
            if (state.status.session.Subproject) {
                name = state.status.session.Subproject.Project.name;
                if (state.status.session.Subproject.name) {
                    name += ` - ${state.status.session.Subproject.name}`;
                }
            } else if (state.status.session.description) {
                name = state.status.session.description;
            } else {
                name = state.status.session.type || "";
            }
            updateTimeNotification(time, state.status.statusCode === 2, name);
        } else {
            closeTimeNotification();
        }
    }, [time, state.status]);

    const loadStatus = async () => {
        try {
            const data = await getData(authStorage.get());
            if (data.status === 401) {
                authStorage.clear();
                navigate('/login');
                return;
            }
            if (data.status === 400) {
                if (data.refresh) {
                    window.location = '/';
                }
                return;
            }
            dispatch({ type: SET_STATUS, payload: data.payload });
            if (data.payload.statusCode === 0 || data.payload.statusCode === 2) {
                setBtnDisabled(true);
            } else {
                setBtnDisabled(false);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const updateStatus = async () => {
        try {
            const data = await getData(authStorage.get());
            if (data.status === 401) {
                authStorage.clear();
                navigate('/login');
                return;
            }
            if (data.status === 400) {
                if (data.refresh) {
                    window.location = '/';
                }
                return;
            }
            dispatch({ type: SET_STATUS, payload: data.payload });
            if (data.payload.statusCode === 0 || data.payload.statusCode === 2) {
                setBtnDisabled(true);
            } else {
                setBtnDisabled(false);
            }
        } catch (e) {
            console.error(e);
        }
    };

    useInterval(updateStatus, 15000);

    async function fetchSessions() {
        try {
            setLoading(true);
            const res = await request('/api/v1/time-tracking/sessions', authStorage.get());
            const data = await res.json();
            setSessions(data.payload.sessions);
            setSkip(data.payload.skip);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }

    const next = useCallback(async () => {
        try {
            const res = await request(`/api/v1/time-tracking/sessions?skip=${skip}`, authStorage.get());
            const data = await res.json();
            setSessions([...sessions, ...data.payload.sessions]);
            setSkip(data.payload.skip);
        } catch (e) {
            console.error(e);
        }
    }, [sessions, skip]);

    const onClickStopSession = async () => {
        try {
            if (btnDisabled || !state.status?.session) {
                return;
            }
            setBtnDisabled(true);
            const data = await stopSession(authStorage.get(), state.status.session.id);
            if (data.status === 401) {
                authStorage.clear();
                navigate('/login');
                return;
            }
            dispatch({ type: SET_STATUS, payload: data.payload.status });
            setTime(0);
            // Refresh sessions list
            await fetchSessions();
        } catch (e) {
            console.error(e);
        } finally {
            setBtnDisabled(false);
        }
    };

    const startTypeSession = async (type) => {
        try {
            if (btnDisabled || state.status?.session?.type === type) {
                return;
            }
            setBtnDisabled(true);
            let desc = null;
            if (type === SESSION_TYPE_FREI_TEXT) {
                const text = prompt("Please enter a description for this session:");
                if (!text || text.trim() === "" || text.length > 255) {
                    setBtnDisabled(false);
                    return;
                }
                desc = text.trim();
            }
            const data = await startSession(authStorage.get(), type, null, desc);
            if (data.status === 401) {
                authStorage.clear();
                navigate('/login');
                return;
            }
            dispatch({ type: SET_STATUS, payload: data.payload.status });
            // Refresh sessions list
            await fetchSessions();
        } catch (e) {
            console.error(e);
        } finally {
            setBtnDisabled(false);
        }
    };

    const onClickLogout = () => {
        authStorage.clear();
        dispatch({ type: SET_STATUS, payload: null });
        setTime(0);
        navigate('/login');
    };

    const handleEditSession = (session) => {
        setEditingSession(session);
    };

    const handleSaveSession = async (updatedSession) => {
        // Update the session in the list
        setSessions(sessions.map(s => s.id === updatedSession.id ? updatedSession : s));
        setEditingSession(null);
        // Refresh to get latest data
        await fetchSessions();
    };

    return (
        <div className="container-fluid p-0" style={{ fontFamily: 'Inter, sans-serif', backgroundColor: '#faf5f0', minHeight: '100vh' }}>
            {/* Header Row */}
            <div className="row g-1 header m-0">
                <div className="col-4 col-lg-2">
                    <div className="hstack gap-1 d-flex justify-content-around w-100">
                        <button className="button" onClick={onClickStopSession}
                            disabled={btnDisabled || !(state.status?.session)}>
                            <img src="/images/clock-out.svg" className="" alt="" />
                        </button>
                        <Popup placement="bottom-start">
                            {({ setPopperElement, setReferenceElement, styles, attributes, isOpen }) => {
                                return (
                                    <>
                                        <button className="button" ref={setReferenceElement}>
                                            <img src="/images/work.svg" className="" alt="" />
                                        </button>
                                        {isOpen && <div className={`list-group`}
                                            ref={setPopperElement}
                                            style={styles.popper}
                                            {...attributes.popper}
                                        >
                                            <button type="button"
                                                className="list-group-item list-group-item-action"
                                                aria-current="true"
                                                disabled={btnDisabled || state.status?.session?.type === SESSION_TYPE_CLEANING}
                                                onClick={() => startTypeSession(SESSION_TYPE_CLEANING)}>
                                                <img src="/images/clean.svg" alt="" />
                                                <span className="ms-1">Cleaning</span>
                                            </button>
                                            <button type="button"
                                                className="list-group-item list-group-item-action"
                                                aria-current="true"
                                                disabled={btnDisabled || state.status?.session?.type === SESSION_TYPE_CALLING}
                                                onClick={() => startTypeSession(SESSION_TYPE_CALLING)}>
                                                <img src="/images/call.svg" alt="" />
                                                <span className="ms-1">Calling</span>
                                            </button>
                                            <button type="button"
                                                className="list-group-item list-group-item-action"
                                                aria-current="true"
                                                disabled={btnDisabled || state.status?.session?.type === SESSION_TYPE_TECHNIK}
                                                onClick={() => startTypeSession(SESSION_TYPE_TECHNIK)}>
                                                <img src="/images/technic.svg" alt="" />
                                                <span className="ms-1">Technik</span>
                                            </button>
                                            <button type="button"
                                                className="list-group-item list-group-item-action"
                                                aria-current="true"
                                                disabled={btnDisabled || state.status?.session?.type === SESSION_TYPE_NEW_LEAD}
                                                onClick={() => startTypeSession(SESSION_TYPE_NEW_LEAD)}>
                                                <img src="/images/new_lead.svg" alt="" />
                                                <span className="ms-1">New lead</span>
                                            </button>
                                            <button type="button"
                                                className="list-group-item list-group-item-action"
                                                aria-current="true"
                                                disabled={btnDisabled || state.status?.session?.type === SESSION_TYPE_MARKETING}
                                                onClick={() => startTypeSession(SESSION_TYPE_MARKETING)}>
                                                <img src="/images/marketing.svg" alt="" />
                                                <span className="ms-1">Marketing</span>
                                            </button>
                                            <button type="button"
                                                className="list-group-item list-group-item-action"
                                                aria-current="true"
                                                disabled={btnDisabled}
                                                onClick={() => startTypeSession(SESSION_TYPE_FREI_TEXT)}>
                                                <img src="/images/frei_text.svg" alt="" />
                                                <span className="ms-1">Frei Text</span>
                                            </button>
                                            <button type="button"
                                                className="list-group-item list-group-item-action"
                                                aria-current="true"
                                                disabled={btnDisabled || state.status?.session?.type === SESSION_TYPE_AKQUISE}
                                                onClick={() => startTypeSession(SESSION_TYPE_AKQUISE)}>
                                                <img src="/images/akquise.svg" alt="" />
                                                <span className="ms-1">Akquise</span>
                                            </button>
                                            <button type="button"
                                                className="list-group-item list-group-item-action"
                                                aria-current="true"
                                                disabled={btnDisabled || state.status?.session?.type === SESSION_TYPE_BUCHHALTUNG}
                                                onClick={() => startTypeSession(SESSION_TYPE_BUCHHALTUNG)}>
                                                <img src="/images/buchhaltung.svg" alt="" />
                                                <span className="ms-1">Buchhaltung</span>
                                            </button>
                                        </div>}
                                    </>
                                )
                            }}
                        </Popup>
                    </div>
                </div>
                <div className="col-4 col-lg-8 d-flex align-items-center justify-content-center">
                    <Time loading={loading} status={state.status} time={time} setTime={setTime} />
                </div>
                <div className="col-4 col-lg-2">
                    <div className="hstack gap-1 d-flex justify-content-around w-100">
                        <button className="button" onClick={onClickLogout}>
                            <img src="/images/logout.svg" className="" alt="" />
                        </button>
                        <Popup placement="bottom-end">
                            {({ setPopperElement, setReferenceElement, styles, attributes, isOpen }) => {
                                return (
                                    <>
                                        <button className="button" ref={setReferenceElement}>
                                            <img src="/images/menu.svg" className="" alt="" />
                                        </button>
                                        {isOpen && <div className={`list-group`}
                                            ref={setPopperElement}
                                            style={styles.popper}
                                            {...attributes.popper}>
                                            <button type="button"
                                                className="list-group-item list-group-item-action"
                                                aria-current="true"
                                                onClick={() => navigate('/')}>
                                                Zurück zur Startseite
                                            </button>
                                            <form action={window.location.port === '3001' ? `${window.location.protocol}//${window.location.hostname}:3000/auth/login/token` : '/auth/login/token'} method="post" className="m-0 p-0">
                                                <input type="hidden" name="next" value={`/statistics/performance/employees/${state.status?.employee?.userId}/quarterly`} />
                                                <input type="hidden" name="x-auth-token" value={authStorage.get()} />
                                                <button type="submit"
                                                    className="list-group-item list-group-item-action popup-dont-close"
                                                >
                                                    Performance Overview
                                                </button>
                                            </form>
                                            <a type="button" href="/change-pin"
                                                className="list-group-item list-group-item-action"
                                                aria-current="true">
                                                PIN ändern
                                            </a>
                                        </div>}
                                    </>
                                )
                            }}
                        </Popup>
                    </div>
                </div>
            </div>

            {/* Info Panel Row */}
            <div className="row m-0">
                <div className="col-12 d-flex justify-content-center align-items-center">
                    <InfoPanel status={state.status} />
                </div>
            </div>

            {/* Sessions Content */}
            <div className="container py-4">
                {/* Header */}
                <div className="mb-4">
                    <h1 className="text-3xl font-bold mb-2" style={{ color: '#3a2e2a' }}>Meine Sessions</h1>
                    <p style={{ color: '#3a2e2a99' }}>Übersicht deiner Zeiterfassungen</p>
                </div>

                {/* Sessions Card */}
                <div className="bg-white rounded-xl p-4 shadow-sm border" style={{ borderColor: '#d9c8b833' }}>
                    {loading && (
                        <div className="d-flex justify-content-center py-5">
                            <span className="loader"></span>
                        </div>
                    )}

                    {!loading && sessions.length === 0 && (
                        <div className="text-center py-5">
                            <p className="text-muted">Keine Sessions vorhanden</p>
                        </div>
                    )}

                    {!loading && sessions.length > 0 && (
                        <SessionTable
                            sessions={sessions}
                            next={next}
                            onEdit={handleEditSession}
                        />
                    )}
                </div>
            </div>

            {/* Edit Modal */}
            {editingSession && (
                <SessionEditModal
                    session={editingSession}
                    onClose={() => setEditingSession(null)}
                    onSave={handleSaveSession}
                />
            )}

            <style jsx>{`
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1050;
                }
                .modal-content {
                    background: white;
                    border-radius: 12px;
                    max-width: 600px;
                    width: 90%;
                    max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
                }
                .modal-header {
                    padding: 1.5rem;
                    border-bottom: 1px solid #d9c8b8;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                .modal-header h3 {
                    margin: 0;
                    color: #3a2e2a;
                    font-weight: 700;
                }
                .modal-close {
                    background: none;
                    border: none;
                    font-size: 2rem;
                    line-height: 1;
                    color: #3a2e2a;
                    cursor: pointer;
                    padding: 0;
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .modal-body {
                    padding: 1.5rem;
                }
                .modal-footer {
                    padding: 1.5rem;
                    border-top: 1px solid #d9c8b8;
                    display: flex;
                    gap: 0.75rem;
                    justify-content: flex-end;
                }
            `}</style>
        </div>
    );
}

export default Sessions;